import { Component, OnInit } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NavbarComponent } from './components/navbar/navbar.component';
import { AuthService } from './services/auth.service';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, NavbarComponent],
  template: `
    <div class="app-container" [class.with-navbar]="showNavbar">
      <app-navbar *ngIf="showNavbar"></app-navbar>
      <main class="main-content" [class.with-navbar]="showNavbar">
        <router-outlet></router-outlet>
      </main>
    </div>
  `,
  styles: [`
    .app-container {
      min-height: 100vh;
      display: flex;
      flex-direction: column;
    }
    
    .main-content {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
    
    .main-content.with-navbar {
      padding-top: 70px; /* Account for fixed navbar height */
    }
    
    /* Ensure full height for auth pages */
    .app-container:not(.with-navbar) .main-content {
      min-height: 100vh;
    }
  `]
})
export class AppComponent implements OnInit {
  title = 'Familitive';
  showNavbar = false;

  constructor(
    public authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Listen to route changes to determine if navbar should be shown
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.updateNavbarVisibility((event as NavigationEnd).url);
        this.checkAuthenticationOnRouteChange((event as NavigationEnd).url);
      });

    // Initial check
    this.updateNavbarVisibility(this.router.url);
    this.checkAuthenticationOnRouteChange(this.router.url);
  }

  private updateNavbarVisibility(url: string): void {
    // Define routes where navbar should NOT be shown
    const authRoutes = ['/login', '/signup', '/forgot-password'];
    
    // Remove query parameters and fragments from URL for matching
    const cleanUrl = url.split('?')[0].split('#')[0];
    
    // Check if current route is an auth route
    const isAuthRoute = authRoutes.some(route => cleanUrl.startsWith(route));
    
    // Show navbar if:
    // 1. User is authenticated (shows user menu)
    // 2. User is NOT authenticated and NOT on auth routes (shows login/signup links)
    this.showNavbar = this.authService.isAuthenticated() || (!this.authService.isAuthenticated() && !isAuthRoute);
  }

  private checkAuthenticationOnRouteChange(url: string): void {
    // Define public routes that don't require authentication
    const publicRoutes = ['/login', '/signup', '/forgot-password', '/privacy-policy', '/terms-of-service'];
    
    // Remove query parameters and fragments from URL for matching
    const cleanUrl = url.split('?')[0].split('#')[0];
    
    // Check if current route is a public route
    const isPublicRoute = publicRoutes.some(route => cleanUrl.startsWith(route));
    
    // If not on a public route, check authentication
    // But add a small delay to allow login redirects to complete
    if (!isPublicRoute) {
      setTimeout(() => {
        this.authService.checkAuthAndRedirect(this.router, url);
      }, 100);
    }
  }
}
